/**
 * 🔷 Skill Shards — Guild-wide upgrade tracks (Lv 1–10 each).
 *
 * Officers / Assistant Leader / Leader can spend Chits from their own
 * wallet to bump a shard's level by 1. Effects apply to EVERY guild
 * member while they're in the guild; removed when they leave.
 *
 * Owner-locked Feb 2026:
 *   • Skill Shard cap = guild's Ashen Apex level (1:1)
 *   • 6 shards: Power · Intellect · Defense (P+M) · Endurance · Charisma · Chit Finder
 *   • Charisma has a special utility ladder (Turn-1 energy, pen aura, skill echo)
 *
 * Endpoints:
 *   GET  /api/guild/skill-shards/state    → catalog + per-shard level/cost
 *   POST /api/guild/skill-shards/upgrade  → bump one shard's level by 1
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtN = (n) => Number(n || 0).toLocaleString();

// Distinct per-shard accent so the 6 cards feel like different artifacts.
const SHARD_THEME = {
  power:       { ring: "ring-red-700/40",     bar: "from-red-700 to-red-500",         glow: "shadow-[0_0_24px_rgba(239,68,68,0.18)]" },
  intellect:   { ring: "ring-purple-700/40",  bar: "from-purple-700 to-fuchsia-500",  glow: "shadow-[0_0_24px_rgba(168,85,247,0.18)]" },
  defense:     { ring: "ring-sky-700/40",     bar: "from-sky-700 to-blue-500",        glow: "shadow-[0_0_24px_rgba(59,130,246,0.18)]" },
  endurance:   { ring: "ring-emerald-700/40", bar: "from-emerald-700 to-green-500",   glow: "shadow-[0_0_24px_rgba(16,185,129,0.18)]" },
  charisma:    { ring: "ring-amber-700/40",   bar: "from-amber-700 to-yellow-400",    glow: "shadow-[0_0_24px_rgba(245,158,11,0.18)]" },
  chit_finder: { ring: "ring-yellow-700/40",  bar: "from-yellow-700 to-amber-500",    glow: "shadow-[0_0_24px_rgba(202,138,4,0.18)]" },
};

/* ───────────────────────────────────────────────────────────────── */

export default function GuildSkillShardsPage() {
  const { isAuthed, loading: authLoading } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);   // shard_id currently upgrading

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/guild/skill-shards/state`);
      setState(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load Skill Shards.");
    }
  }, []);

  useEffect(() => {
    if (authLoading || !isAuthed) return;
    refresh();
  }, [authLoading, isAuthed, refresh]);

  const upgrade = async (shard_id) => {
    setBusy(shard_id);
    try {
      const r = await axios.post(`${API}/guild/skill-shards/upgrade`, { shard_id });
      setState(r.data);
      const shard = r.data.shards.find((s) => s.shard_id === shard_id);
      toast.success(`${shard.name} → Lv ${shard.level}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade failed.");
    } finally {
      setBusy(null);
    }
  };

  if (authLoading) return null;
  if (!isAuthed) {
    return (
      <Shell>
        <div className="max-w-3xl mx-auto px-4 py-16 text-center text-neutral-400" data-testid="skill-shards-auth-required">
          🔒 Sign in to view Skill Shards.
        </div>
      </Shell>
    );
  }
  if (!state) {
    return (
      <Shell>
        <div className="max-w-3xl mx-auto px-4 py-16 text-center text-neutral-500" data-testid="skill-shards-loading">
          Tuning the shards…
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="max-w-6xl mx-auto px-4 py-6 pb-24" data-testid="skill-shards-page">
        <HeroBanner state={state} />
        {!state.in_guild ? (
          <NotInGuildPanel reason={state.reason} />
        ) : (
          <>
            <ShardGrid
              shards={state.shards}
              wallet={state.wallet}
              canUpgrade={state.can_upgrade}
              guildCap={state.guild_cap}
              busyShardId={busy}
              onUpgrade={upgrade}
            />
            {!state.can_upgrade && (
              <div
                className="mt-6 text-center text-sm text-amber-300 bg-amber-950/30 border border-amber-700/40 rounded-xl py-3 px-4"
                data-testid="skill-shards-read-only-banner"
              >
                ⚠️ {state.reason}
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  );
}

/* ───────────────────────────────────────────────────────────────── */

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200">
      <Header />
      {children}
      <Footer />
    </div>
  );
}

function HeroBanner({ state }) {
  return (
    <div
      className="mb-6 rounded-2xl border border-amber-700/40 bg-gradient-to-br from-[#1a1305] via-[#0A0A0A] to-[#1a0a05] p-5 shadow-[0_0_30px_rgba(245,158,11,0.18)]"
      data-testid="skill-shards-hero"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div
            className="text-3xl font-black tracking-wide text-amber-300"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            🔷 SKILL SHARDS
          </div>
          <div className="text-xs text-neutral-400 mt-1 leading-relaxed max-w-2xl">
            Guild-wide upgrade tracks. Bonuses apply to <span className="text-amber-300">every member</span> while
            they're in this guild — and vanish the moment they leave. Cap rises with the guild's
            {" "}<Link to="/guild/ashen-apex" className="text-amber-300 underline hover:text-amber-200">Ashen Apex</Link> level.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {state.in_guild && (
            <Pill icon="🏰" label="Guild" value={state.guild_name} testid="shards-pill-guild" />
          )}
          {state.in_guild && (
            <Pill icon="🌳" label="Apex Cap" value={`Lv ${state.guild_cap}`} testid="shards-pill-cap" />
          )}
          <Pill icon="🪙" label="Chits" value={fmtN(state.wallet?.chits)} testid="shards-pill-chits" />
          {state.role && <RoleChip role={state.role} />}
        </div>
      </div>
    </div>
  );
}

function Pill({ icon, label, value, testid }) {
  return (
    <div
      className="px-3 py-1.5 rounded-full bg-black/40 border border-neutral-700 text-sm flex items-center gap-1.5"
      data-testid={testid}
    >
      <span>{icon}</span>
      <span className="font-bold text-amber-300">{value}</span>
      <span className="text-neutral-500 text-xs">{label}</span>
    </div>
  );
}

function RoleChip({ role }) {
  const map = {
    leader:           { icon: "👑", label: "Leader",            cls: "bg-amber-700/60 text-amber-50 border-amber-400" },
    assistant_leader: { icon: "🦅", label: "Asst. Leader",      cls: "bg-amber-800/40 text-amber-100 border-amber-600" },
    officer:          { icon: "🛡️", label: "Officer",           cls: "bg-amber-900/40 text-amber-200 border-amber-700" },
    member:           { icon: "🪶", label: "Member",            cls: "bg-neutral-800 text-neutral-300 border-neutral-700" },
  };
  const m = map[role] || map.member;
  return (
    <div
      className={`px-3 py-1.5 rounded-full border text-sm flex items-center gap-1.5 ${m.cls}`}
      data-testid={`shards-role-${role}`}
    >
      <span>{m.icon}</span>
      <span className="font-bold">{m.label}</span>
    </div>
  );
}

function NotInGuildPanel({ reason }) {
  return (
    <div
      className="rounded-xl border border-neutral-800 bg-neutral-950 p-8 text-center"
      data-testid="skill-shards-no-guild"
    >
      <div className="text-5xl mb-3">🏰</div>
      <div className="text-lg font-bold text-amber-200 mb-1">No guild, no shards</div>
      <div className="text-sm text-neutral-400 mb-4 max-w-md mx-auto">
        {reason || "Join a guild to access Skill Shards. Buffs apply while you're a member."}
      </div>
      <Link
        to="/guild"
        className="inline-block px-4 py-2 rounded-lg bg-amber-700 hover:bg-amber-600 text-white text-sm font-bold transition"
        data-testid="skill-shards-find-guild-cta"
      >
        Find a Guild →
      </Link>
    </div>
  );
}

/* ───────── 6-card shard grid ───────── */

function ShardGrid({ shards, wallet, canUpgrade, guildCap, busyShardId, onUpgrade }) {
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
      data-testid="skill-shards-grid"
    >
      {shards.map((s) => (
        <ShardCard
          key={s.shard_id}
          shard={s}
          wallet={wallet}
          canUpgrade={canUpgrade}
          guildCap={guildCap}
          busy={busyShardId === s.shard_id}
          onUpgrade={() => onUpgrade(s.shard_id)}
        />
      ))}
    </div>
  );
}

function ShardCard({ shard, wallet, canUpgrade, guildCap, busy, onUpgrade }) {
  const theme = SHARD_THEME[shard.shard_id] || SHARD_THEME.power;
  const pct = (shard.level / shard.max_level) * 100;
  const canAfford = (wallet?.chits || 0) >= (shard.next_cost_chits || 0);

  return (
    <div
      className={`rounded-2xl border border-neutral-800 bg-gradient-to-br from-neutral-950 to-black p-4 ${theme.glow} ring-1 ${theme.ring}`}
      data-testid={`shard-card-${shard.shard_id}`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-3xl">{shard.icon}</span>
          <div>
            <div className="text-sm font-bold text-amber-100 leading-tight">{shard.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-0.5">
              {shard.stat_label}
            </div>
          </div>
        </div>
        <div
          className="text-right"
          data-testid={`shard-${shard.shard_id}-level`}
        >
          <div className="text-2xl font-black text-amber-300 leading-none">
            Lv {shard.level}
          </div>
          <div className="text-[10px] text-neutral-500 uppercase tracking-widest">
            / {shard.max_level}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 w-full bg-neutral-900 rounded-full overflow-hidden mb-3">
        <div
          className={`h-full bg-gradient-to-r ${theme.bar} transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Current value */}
      <div
        className="text-xs text-neutral-400 mb-2"
        data-testid={`shard-${shard.shard_id}-current`}
      >
        Current: <span className="text-amber-200 font-bold">{formatValue(shard, shard.current_value)}</span>
      </div>

      {/* Charisma utility — special block */}
      {shard.shard_id === "charisma" && shard.current_utility && (
        <CharismaUtility tier={shard.current_utility} nextTier={shard.next_utility} />
      )}

      {/* Next-tier preview */}
      {!shard.is_max && shard.next_value !== null && (
        <div
          className="text-[11px] text-neutral-500 mb-3"
          data-testid={`shard-${shard.shard_id}-next`}
        >
          Next: Lv {shard.level + 1} →{" "}
          <span className="text-emerald-300 font-bold">
            {formatValue(shard, shard.next_value)}
          </span>
        </div>
      )}

      {/* Action row */}
      <ShardActionButton
        shard={shard}
        canUpgrade={canUpgrade}
        canAfford={canAfford}
        guildCap={guildCap}
        busy={busy}
        onUpgrade={onUpgrade}
      />
    </div>
  );
}

function formatValue(shard, value) {
  if (value === null || value === undefined) return "—";
  if (shard.shard_id === "chit_finder") return `+${value}% gold find`;
  if (shard.shard_id === "charisma")    return `+${value} Charisma`;
  if (shard.shard_id === "defense")     return `+${value} PDEF & MDEF`;
  return `+${value} ${shard.stat_label}`;
}

function ShardActionButton({ shard, canUpgrade, canAfford, guildCap, busy, onUpgrade }) {
  // Max-cap of the system (Lv 10)
  if (shard.is_max) {
    return (
      <div
        className="w-full py-2 text-center text-[11px] uppercase tracking-widest rounded-lg border border-amber-700/40 bg-amber-950/30 text-amber-300"
        data-testid={`shard-${shard.shard_id}-maxed`}
      >
        ✨ Maxed Out
      </div>
    );
  }
  // Guild-cap locked (Apex level < required)
  if (shard.cap_locked) {
    return (
      <Link
        to="/guild/ashen-apex"
        className="block w-full py-2 text-center text-[11px] uppercase tracking-widest rounded-lg border border-purple-700/50 bg-purple-950/40 text-purple-200 hover:bg-purple-900/50 transition"
        data-testid={`shard-${shard.shard_id}-cap-locked`}
        title="Upgrade your guild's Ashen Apex level to unlock the next shard tier."
      >
        🔒 Upgrade Apex (Lv {guildCap} cap)
      </Link>
    );
  }
  // No role permission
  if (!canUpgrade) {
    return (
      <div
        className="w-full py-2 text-center text-[11px] uppercase tracking-widest rounded-lg border border-neutral-800 bg-neutral-950 text-neutral-500"
        data-testid={`shard-${shard.shard_id}-no-role`}
      >
        Officer rank required
      </div>
    );
  }
  // Affordable upgrade
  return (
    <button
      onClick={onUpgrade}
      disabled={busy || !canAfford}
      data-testid={`shard-${shard.shard_id}-upgrade-btn`}
      className={`w-full py-2 text-[11px] uppercase tracking-widest font-bold rounded-lg transition ${
        canAfford
          ? "bg-amber-700 hover:bg-amber-600 text-white"
          : "bg-neutral-900 border border-neutral-800 text-neutral-500 cursor-not-allowed"
      } disabled:opacity-60`}
      title={!canAfford ? `Need ${fmtN(shard.next_cost_chits)} 🪙 Chits` : ""}
    >
      {busy
        ? "Upgrading…"
        : `Upgrade · ${fmtN(shard.next_cost_chits)} 🪙`}
    </button>
  );
}

/* ───────── Charisma special block ───────── */

function CharismaUtility({ tier, nextTier }) {
  if (!tier || tier.level === 0) {
    return (
      <div
        className="text-[10px] text-neutral-600 italic mb-2"
        data-testid="charisma-utility-locked"
      >
        Unlock Lv 1 to begin the utility ladder.
      </div>
    );
  }
  return (
    <div
      className="mb-3 px-2 py-1.5 rounded-lg bg-amber-950/30 border border-amber-700/30"
      data-testid="charisma-utility-block"
    >
      <div className="text-[9px] uppercase tracking-widest text-amber-400 mb-1">
        ✨ Utility Tier · Lv {tier.level}
      </div>
      <div className="text-[10px] text-amber-100 leading-relaxed">
        <div data-testid="charisma-starting-energy">
          ⚡ +{tier.starting_energy_pct}% Starting Energy
        </div>
        <div data-testid="charisma-pen-aura">
          🩸 +{tier.pen_aura_pct}% Armor & Magic Pen aura
        </div>
        {tier.skill_echo_pct > 0 && (
          <div data-testid="charisma-skill-echo">
            🔁 {tier.skill_echo_pct}% Skill Echo chance
          </div>
        )}
        {tier.guarantee_mid_tier_on_t1 && (
          <div className="text-emerald-300" data-testid="charisma-guarantee-mid">
            ✓ Mid-tier skill guaranteed on Turn 1
          </div>
        )}
        {tier.guarantee_ult_on_t1 && (
          <div className="text-fuchsia-300 font-bold" data-testid="charisma-guarantee-ult">
            ✨ Ultimate guaranteed on Turn 1
          </div>
        )}
      </div>
      {nextTier && nextTier.level !== tier.level && (
        <div className="text-[9px] text-neutral-500 mt-1 italic">
          Next tier unlocks at Lv {nextTier.level}.
        </div>
      )}
    </div>
  );
}
